

































































































































































































































































import {
  defineComponent,
  useFetch,
  ref,
  nextTick,
  watch,
  useFetchState
} from '~/utils/nuxt3-migration'
import CForm from '~/components/shared/configurable/form/CForm.vue'
import CFormError from '~/components/shared/configurable/form/CFormError.vue'
import CFormGroup from '~/components/shared/configurable/form/CFormGroup.vue'
import CInternationalPostcodeInput from '~/components/shared/configurable/form/input/CInternationalPostcodeInput.vue'
import CInternationalTelephoneInput from '~/components/shared/configurable/form/input/CInternationalTelephoneInput.vue'
import CCustomSelect from '~/components/shared/configurable/form/select/custom/CCustomSelect.vue'
import { useDeps } from '~/compositions/dependency-container'
import { useI18n } from '~/compositions/i18n'
import { useRequestError } from '~/compositions/request-error'
import { useSnackbar } from '~/compositions/snackbar'
import { ManagerApplicationForm } from '~/models/user/types'
import { FormFieldErrors, Schema } from '~/models/form/types'
import ManagerApplicationService from '~/services/managers/ManagerApplicationService'
import ScrollService from '~/services/scroll/ScrollService'
import { defineComponentTranslations } from '~/utils/i18n'
import { toCamelCase } from '~/utils/object'
import SuccessCheckmarkAnimation from '~/components/shared/account/payments/SuccessCheckmarkAnimation.vue'
import CPanelCard from '~/components/shared/configurable/cpanel/CPanelCard.vue'

export default defineComponent({
  components: {
    CPanelCard,
    CForm,
    CFormGroup,
    CCustomSelect,
    CInternationalPostcodeInput,
    CInternationalTelephoneInput,
    CFormError,
    SuccessCheckmarkAnimation
  },
  setup() {
    const [managerApplicationService, scrollService] = useDeps(
      ManagerApplicationService,
      ScrollService
    )
    const handleRequestError = useRequestError()
    const snackbar = useSnackbar()
    const { t } = useI18n()

    const submitSuccess = ref(false)
    const loading = useFetchState('loading', ref(false))
    const schema = useFetchState('schema', ref<Schema>({}))
    const values = ref<ManagerApplicationForm>({
      postcode: {
        country: { id: 1 },
        postcode: {}
      }
    } as ManagerApplicationForm)
    const errors = useFetchState('errors', ref<FormFieldErrors>({}))
    const acceptTerms = ref(false)

    watch(errors, scrollToFirstError)

    useFetch(async () => {
      loading.value = true
      try {
        const data = await managerApplicationService.applicationFormSchema()
        schema.value = data.schema
      } catch (error) {
        const fieldErrors = (error as any).response?.data?.data?.errors
        if (fieldErrors) {
          errors.value = toCamelCase(fieldErrors)
        }
        handleRequestError(error)
      } finally {
        loading.value = false
      }
    })

    async function submit() {
      if (!acceptTerms.value) return
      loading.value = true
      errors.value = {}
      try {
        await managerApplicationService.submitApplication(values.value)
        snackbar.success(t('form_submitted_successfully'))
        values.value = {} as ManagerApplicationForm
        submitSuccess.value = true
        await nextTick()
        scrollService.scrollToTopSmooth()
      } catch (error) {
        const fieldErrors = (error as any).response?.data?.data?.errors
        if (fieldErrors) {
          errors.value = toCamelCase(fieldErrors)
        }
        handleRequestError(error)
      } finally {
        loading.value = false
      }
    }
    async function scrollToFirstError() {
      await nextTick()

      const firstError = document.querySelector('[aria-invalid]')

      if (firstError) {
        scrollService.scrollTo(firstError, {
          duration: 300,
          easing: 'ease-out',
          offset: -100
        })
      }
    }

    return {
      submitSuccess,
      loading,
      schema,
      values,
      errors,
      acceptTerms,
      submit
    }
  },
  head() {
    return {
      title: this.$t('dealer registration').toString(),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.$t('dealer registration').toString()
        }
      ]
    }
  },
  i18n: defineComponentTranslations({
    register_alert_1: {
      en:
        'You have <strong>2 months</strong> of free trial and are not obligated to continue after the trial period.',
      el:
        'Έχεις <strong>2 μήνες δωρεάν</strong> δοκιμής και δεν είσαι υποχρεωμένος να συνεχίσεις μετά την δοκιμαστική περίοδο.'
    },
    register_alert_2: {
      en:
        'For bulk ad uploads to Car.gr, send us your XML file. Instructions can be found at',
      el:
        'Για μαζική εισαγωγή αγγελιών στο Car.gr στείλε μας το αρχείο XML σου. Οδηγίες θα βρείς στο'
    },
    why_register: {
      en: 'Why register as a dealer',
      el: 'Γιατί να αποκτήσω λογαριασμού εμπόρου'
    },
    dealer_info: {
      en: 'Dealer information',
      el: 'Στοιχεία εμπόρου'
    },
    dealer_info_desc: {
      en:
        'These are your login details for accessing the system and managing your website. The username is used for your website, e.g.: https://manos-cars.car.gr or https://www.car.gr/manos-cars',
      el:
        'Αυτά είναι τα στοιχεία σου για να συνδεθείς με το σύστημα και να διαχειρίζεσαι την σελίδα σου. Το όνομα χρήστη χρησιμοποιείται για την σελίδα σου, πχ: https://manos-cars.car.gr ή https://www.car.gr/manos-cars'
    },
    desired_username: {
      en: 'Desired username',
      el: 'Επιθυμητό όνομα χρήστη'
    },
    desired_username_desc: {
      en:
        'Username can contain Latin characters and hyphen (-) e.g. manos-cars.',
      el:
        'Το όνομα χρήστη μπορεί να περιέχει λατινικούς χαρακτήρες και παύλα (-) πχ. manos-cars.'
    },
    application_received: {
      en:
        'We received your application. We will process it and get back to you soon.',
      el:
        'Λάβαμε την αίτηση σου. Θα την επεξεργαστούμε και θα επικοινωνήσουμε σύντομα μαζί σου.'
    },
    form_submitted_successfully: {
      en: 'Application submitted successfully!',
      el: 'Η αίτηση υποβλήθηκε με επιτυχία!'
    },
    mobile_or_landline: {
      en: 'Mobile or Landline',
      el: 'Κινητό ή σταθερό'
    }
  })
})
